import { useEffect } from 'react';

import { faPlus, faTimes } from '@fortawesome/pro-light-svg-icons';
import dayjs, { Dayjs } from 'dayjs';
import { DutyHourTypeDynFlag } from 'generated';
import { Control, UseFormWatch, useFieldArray } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { InferType } from 'yup';

import { Box } from '@mui/material';
import Button from '@mui/material/Button';
import Grid from '@mui/material/Grid';
import IconButton from '@mui/material/IconButton';

import { FontAwesomeSvgIcon, ControlledTimePicker } from 'components/shared';
import ControlledDutyHourTypeSelector from 'components/shared/ControlledFields/ControlledDutyHourTypeSelector';

import getPeriodsSchema from './ControlledTimePeriodsSchema';

type TimePeriodsProps = {
  control: Control<any>;
  hasAddPeriods?: boolean;
  hasDHTSelector?: boolean;
  hideBreakDuration?: boolean;
  dutyHourTypeDynFlags?: DutyHourTypeDynFlag[];
  referenceDate?: Date;
  watch: UseFormWatch<any>;
  baseDate?: Dayjs;
  checkRestrictedAuthorization?: boolean;
  disabled?: boolean;
};

const periodsSchema = getPeriodsSchema();
export type PeriodsForm = InferType<typeof periodsSchema>;

type Period = {
  dutyCodePeriodId?: number;
  startTime: Dayjs;
  endTime: Dayjs;
  dutyHourTypeId: number;
};

type FormValues = {
  periods: Period[];
};

export default function TimePeriods({
  control,
  hasAddPeriods = true,
  hasDHTSelector,
  hideBreakDuration,
  dutyHourTypeDynFlags,
  referenceDate,
  watch,
  baseDate: b,
  checkRestrictedAuthorization,
  disabled
}: TimePeriodsProps) {
  const { t } = useTranslation('common');
  const baseDate = (b || dayjs('1969-12-31T23:00:00Z')).startOf('day');

  const {
    fields: periods,
    append,
    remove,
    update
  } = useFieldArray<FormValues, 'periods', 'id'>({
    name: 'periods',
    control
  });

  function onAddClick() {
    const lastPeriodEndTime = watch(`periods.${periods.length - 1}.endTime`);

    const value = {
      dutyCodePeriodId: undefined,
      startTime: lastPeriodEndTime,
      endTime: lastPeriodEndTime.add(1, 'hour'),
      dutyHourTypeId: -1
    };

    append(value);
  }

  function changeTime(time: Dayjs | null, index: number, name: string) {
    if (time && time.isValid()) {
      let lastTime = baseDate;
      let oneDayAhead = lastTime.add(1, 'day');
      for (let i = 0; i < periods.length; i++) {
        let changed = false;
        if (index === i && name === 'startTime') {
          periods[i].startTime = time;
          changed = true;
        }
        if (!periods[i].startTime.isBefore(oneDayAhead)) {
          periods[i].startTime = periods[i].startTime.subtract(1, 'day');
          changed = true;
        }
        if (periods[i].startTime.isBefore(lastTime)) {
          periods[i].startTime = periods[i].startTime.add(1, 'day');
          changed = true;
        }
        lastTime = periods[i].startTime;
        oneDayAhead = lastTime.add(1, 'day');

        if (index === i && name === 'endTime') {
          periods[i].endTime = time;
          changed = true;
        }
        if (periods[i].endTime.isAfter(oneDayAhead)) {
          periods[i].endTime = periods[i].endTime.subtract(1, 'day');
          changed = true;
        }
        if (!periods[i].endTime.isAfter(lastTime)) {
          periods[i].endTime = periods[i].endTime.add(1, 'day');
          changed = true;
        }
        lastTime = periods[i].endTime;
        oneDayAhead = lastTime.add(1, 'day');
        if (changed) {
          update(i, periods[i]);
        }
      }
    }
  }
  useEffect(() => {
    changeTime(periods[0].startTime, -1, 'startTime');
  }, []);

  return (
    <Box mt={2.5}>
      {periods.map((period, i) => {
        return (
          <Grid container spacing={2.5} key={period.id} alignItems="flex-start">
            <Grid item xs={hasDHTSelector ? 3 : 5}>
              <ControlledTimePicker
                fullWidth
                variant="outlined"
                control={control}
                name={`periods.${i}.startTime`}
                label={t('startTime')}
                onChange={(time) => changeTime(time, i, 'startTime')}
                baseDate={baseDate}
                disabled={disabled}
              />
            </Grid>
            <Grid item xs={hasDHTSelector ? 3 : 5}>
              <ControlledTimePicker
                fullWidth
                variant="outlined"
                control={control}
                name={`periods.${i}.endTime`}
                label={t('endTime')}
                onChange={(time) => changeTime(time, i, 'endTime')}
                baseDate={baseDate}
                disabled={disabled}
              />
            </Grid>
            {hasDHTSelector && (
              <Grid item xs={5}>
                <ControlledDutyHourTypeSelector
                  control={control}
                  name={`periods.${i}.dutyHourTypeId`}
                  label={t('dutyHourType')}
                  fullWidth
                  filterDutyHourTypeDynFlags={dutyHourTypeDynFlags}
                  referenceDate={referenceDate}
                  checkRestrictedAuthorization={checkRestrictedAuthorization}
                  disabled={disabled}
                />
              </Grid>
            )}
            <Grid
              item
              xs={hasDHTSelector ? 1 : 2}
              className="d-flex v-bottom end"
            >
              <IconButton
                style={{ marginTop: '4px' }}
                onClick={() => remove(i)}
                color="primary"
                size="small"
                disabled={disabled || i === 0}
              >
                <FontAwesomeSvgIcon icon={faTimes} fontSize="small" />
              </IconButton>
            </Grid>
            {!hideBreakDuration && i < periods.length - 1 && (
              <Grid item xs={12} mb={2}>
                {periods[i + 1].startTime.diff(period.endTime, 'minute')}m{' '}
                {t('pauseBreak')}
              </Grid>
            )}
            {hideBreakDuration && <Grid item xs={12} />}
          </Grid>
        );
      })}
      {hasAddPeriods && !disabled && (
        <Grid item xs={12} mt={2}>
          <Button
            variant="outlined"
            size="small"
            onClick={onAddClick}
            startIcon={<FontAwesomeSvgIcon icon={faPlus} fontSize="small" />}
          >
            {t('addTimes')}
          </Button>
        </Grid>
      )}
    </Box>
  );
}
